/**
 * Marker attached to validators that semantically mark a field as required.
 * `mdyForm()` reads it to drive the field's `required` signal (aria-required)
 * without needing a separate flag in the schema.
 */
export const MDY_MARKS_REQUIRED = Symbol("mdyMarksRequired");
/** Marker carrying the constraints a validator enforces. */
export const MDY_VALIDATOR_FACTS = Symbol("mdyValidatorFacts");
/** A field with no rules constrains nothing. */
export const NO_CONSTRAINTS = {
    min: null,
    max: null,
    step: null,
    minLength: null,
    maxLength: null,
    pattern: null,
    inputMode: null,
};
const MDY_VALIDATOR_DECLARATION = Symbol("mdyValidatorDeclaration");
/** How a document names this rule, when the rule says so. `undefined` for one that does not. */
export function declarationOf(fn) {
    if (typeof fn !== "function")
        return undefined;
    return Reflect.get(fn, MDY_VALIDATOR_DECLARATION);
}
export function withFacts(fn, facts, declaration) {
    // A wrapper rather than a tag on the caller's function: this is exported, so the function handed
    // in may be one the caller uses elsewhere, and hanging facts on it would change a rule they did
    // not ask to change.
    const declared = (value) => fn(value);
    const tagged = Object.assign(declared, { [MDY_VALIDATOR_FACTS]: { ...factsOf(fn), ...facts } });
    // Carried on the wrapper, not merged from the wrapped function: a rule declares its own name or
    // has none, and inheriting one would let a rule built out of another answer to a name that
    // belongs to something else.
    return declaration === undefined
        ? tagged
        : Object.assign(tagged, { [MDY_VALIDATOR_DECLARATION]: declaration });
}
/** What a single rule declares, including the standalone required marker an adapter may have set. */
export function factsOf(fn) {
    if (typeof fn !== "function")
        return {};
    const facts = Reflect.get(fn, MDY_VALIDATOR_FACTS);
    const required = Reflect.get(fn, MDY_MARKS_REQUIRED) === true;
    if (facts && required)
        return { ...facts, required: true };
    if (facts)
        return facts;
    return required ? { required: true } : {};
}
/**
 * The sum of what a list of rules declares.
 *
 * Where two rules bound the same thing the **tightest wins**: each was added to exclude something,
 * and widening either would admit what one of them refuses. A bound that is not a finite number
 * states nothing an input can carry — `min="NaN"` is ignored by the browser and misleading in a
 * diff — so it is dropped while its rule still runs.
 *
 * Two *different* patterns cannot be combined into one: an input carries a single `pattern`, and
 * inventing an intersection would be a rule nobody wrote. The field then offers none, and both
 * rules stay in force. {@link mergeFacts} reports that case so a caller can say so.
 */
export function mergeFacts(facts) {
    /**
     * How each constraint combines with another of its kind.
     *
     * A table rather than a chain of branches: a fact added tomorrow does not compile until it says
     * how two of them add up, which is the one thing about it that cannot be guessed.
     */
    const COMBINE = {
        min: (a, b) => Math.max(a, b),
        max: (a, b) => Math.min(a, b),
        // A coarser step admits fewer values, so it is the tighter of two.
        step: (a, b) => Math.max(a, b),
        minLength: (a, b) => Math.max(a, b),
        maxLength: (a, b) => Math.min(a, b),
    };
    const numbers = {
        min: null, max: null, step: null, minLength: null, maxLength: null,
    };
    let required = false;
    let inputMode = null;
    const patterns = new Set();
    for (const fact of facts) {
        if (fact.required === true)
            required = true;
        for (const key of Object.keys(COMBINE)) {
            const stated = fact[key];
            // A bound that is not a finite number states nothing an input can carry — `min="NaN"` is
            // ignored by the browser and misleading in a diff — so it is dropped while its rule runs.
            if (stated === undefined || !Number.isFinite(stated))
                continue;
            const held = numbers[key];
            numbers[key] = held === null ? stated : COMBINE[key](held, stated);
        }
        if (fact.pattern !== undefined)
            patterns.add(fact.pattern);
        if (fact.inputMode !== undefined)
            inputMode ??= fact.inputMode;
    }
    const conflictingPatterns = patterns.size > 1;
    return {
        required,
        conflictingPatterns,
        constraints: {
            ...numbers,
            pattern: conflictingPatterns ? null : nativePatternSource([...patterns][0] ?? null),
            inputMode,
        },
    };
}
/** The sum of what a list of validators declares. */
/**
 * A rule's expression, padded to mean the same thing inside an implicitly anchored attribute.
 *
 * An expression already carrying `^` or `$` is left as it is: there the two readings agree. One the
 * platform cannot compile carries no attribute at all, because an invalid `pattern` is ignored by
 * the browser — a rule that looks enforced and is not.
 */
function nativePatternSource(source) {
    if (source === null || source === "")
        return source;
    const opensAnchored = source.startsWith("^");
    const closesAnchored = source.endsWith("$");
    // Anchored at both ends the rule already means what the attribute means, and it is written out
    // unchanged: a group around it changes nothing a browser does and everything a person reads, and
    // the attribute is read — in the DOM, in a screenshot, in a report of what the control asks for.
    // The group is there for the padding: `.*a|b.*` binds the alternation across the padding, where
    // `.*(?:a|b).*` is the rule with room either side of it.
    const projected = opensAnchored && closesAnchored
        ? source
        : `${opensAnchored ? "" : ".*"}(?:${source})${closesAnchored ? "" : ".*"}`;
    try {
        new RegExp(projected);
    }
    catch {
        return null;
    }
    return projected;
}
export function factsOfAll(validators) {
    return mergeFacts([...validators].map((fn) => factsOf(fn)));
}
